import { Reducer, Action } from 'redux';
import {
  ACTION, ReceiveLanguageOptions,
  RequestSubscriberGroup, ReceiveSubscriberGroup, FailSubscriberGroup,
  RequestSaveSubscriberGroup, ReceiveSaveSubscriberGroup, FailSaveSubscriberGroup,
  UpdateItem, ChangeLanguage, CopyLanguageToAll,
  ClearForm, UpdateValidation,
  ChangeLongDescription,
  CopyLongDescriptionToAll,
  UpdateExternalMemberItem,
  RemoveExternalMember,
  RemoveAllExternalMembers,
  AddExternalMember,
  ResetValidation,
  ResetAllExternalMemberValidation,
  UpdateMergeSubscriberGroup,
  RequestSubscriberGroupOptions,
  ReceiveSubscriberGroupOptions,
  FailSubscriberGroupOptions,
  RequestSubscriberGroupItemDelete,
  ReceiveSubscriberGroupItemDelete,
  FailSubscriberGroupItemDelete
} from './action-types';
import { languageValidationParameter, longDescriptionValidationParameter } from './constants';
import {
  SubscriberGroupsFormState as SubscriberGroupsFormState, SubscriberGroupItem
} from './types-store';
import { createId, createInitialExternalMember } from 'app/area/subscriber-groups/form/utils/helpers';
import { withOptionsReducer } from 'app/components/options-handler';
import { STATE } from 'app/constants';
import {
  clearValidationForKeys, clearValidationForAllParameters, clearValidationForSingleParameter
} from 'app/area/subscriber-groups/form/utils/validation';

export const INITIAL_STATE: SubscriberGroupsFormState = {
  actionType: undefined,
  subscriberGroupItem: undefined,
  originalSubscriberGroupItem: undefined,
  fetching: false,
  saving: false,
  languageOptions: [],
  mergeSubscriberGroup: undefined,
  validation: {}
};

const reducers: { [indexer: string]: Reducer<SubscriberGroupsFormState, Action> } = {

  [ACTION.RECEIVE_LANGUAGE_OPTIONS]: (state: SubscriberGroupsFormState,
    action: ReceiveLanguageOptions): SubscriberGroupsFormState => {

    return {
      ...state,
      languageOptions: action.options
    };
  },

  [ACTION.REQUEST_SUBSCRIBER_GROUP]: (state: SubscriberGroupsFormState,
    action: RequestSubscriberGroup): SubscriberGroupsFormState => {

    return {
      ...state,
      subscriberGroupItem: undefined,
      originalSubscriberGroupItem: undefined,
      fetching: true,
      saving: false,
      validation: {}
    };
  },

  [ACTION.RECEIVE_SUBSCRIBER_GROUP]: (state: SubscriberGroupsFormState,
    action: ReceiveSubscriberGroup): SubscriberGroupsFormState => {

    return {
      ...state,
      subscriberGroupItem: action.item,
      originalSubscriberGroupItem: action.item,
      fetching: false
    };
  },

  [ACTION.FAIL_SUBSCRIBER_GROUP]: (state: SubscriberGroupsFormState,
    action: FailSubscriberGroup): SubscriberGroupsFormState => {

    return {
      ...state,
      fetching: false
    };
  },

  [ACTION.REQUEST_SAVE_SUBSCRIBER_GROUP]: (state: SubscriberGroupsFormState,
    action: RequestSaveSubscriberGroup): SubscriberGroupsFormState => {

    return {
      ...state,
      saving: true
    };
  },

  [ACTION.RECEIVE_SAVE_SUBSCRIBER_GROUP]: (state: SubscriberGroupsFormState,
    action: ReceiveSaveSubscriberGroup): SubscriberGroupsFormState => {

    return {
      ...state,
      saving: false,
      originalSubscriberGroupItem: state.subscriberGroupItem
    };
  },

  [ACTION.FAIL_SAVE_SUBSCRIBER_GROUP]: (state: SubscriberGroupsFormState,
    action: FailSaveSubscriberGroup): SubscriberGroupsFormState => {

    return {
      ...state,
      saving: false
    };
  },

  [ACTION.CHANGE_LANGUAGE]: (state: SubscriberGroupsFormState,
    action: ChangeLanguage): SubscriberGroupsFormState => {

    const subscriberGroupItem = state.subscriberGroupItem || {} as SubscriberGroupItem;
    const language = subscriberGroupItem.language || [];

    return {
      ...state,
      subscriberGroupItem: {
        ...subscriberGroupItem,
        language: language.some(languageItem => languageItem.value === action.languageId)
          ? language.map(
            languageItem => languageItem.value === action.languageId
              ? {
                ...languageItem,
                description: action.description
              }
              : languageItem
          )
          : [
            ...language,
            {
              value: action.languageId,
              description: action.description
            }
          ]
      },
      validation: clearValidationForSingleParameter(state.validation, 'language', languageValidationParameter, action.languageId)
    };
  },

  [ACTION.COPY_LANGUAGE_TO_ALL]: (state: SubscriberGroupsFormState,
    action: CopyLanguageToAll): SubscriberGroupsFormState => {

    const subscriberGroupItem = state.subscriberGroupItem || {} as SubscriberGroupItem;
    const language = subscriberGroupItem.language || [];
    const targetLanguage = language.find(languageItem => languageItem.value === action.languageId);

    if (targetLanguage === undefined) {
      return state;
    }

    const updatedLanguage = language.map(
      languageItem => ({
        ...languageItem,
        description: targetLanguage.description
      })
    );
    const newLanguage = (state.languageOptions || [])
      .map(option => option.value)
      .filter(languageId => !language.some(languageItem => languageItem.value === languageId))
      .map(languageId => ({
        value: languageId,
        description: targetLanguage.description
      }));

    return {
      ...state,
      subscriberGroupItem: {
        ...subscriberGroupItem,
        language: [
          ...updatedLanguage,
          ...newLanguage
        ]
      },
      validation: clearValidationForAllParameters(state.validation, 'language', languageValidationParameter)
    };
  },

  [ACTION.CHANGE_LONG_DESCRIPTION]: (state: SubscriberGroupsFormState,
    action: ChangeLongDescription): SubscriberGroupsFormState => {

    const subscriberGroupItem = state.subscriberGroupItem || {} as SubscriberGroupItem;
    const longDescription = subscriberGroupItem.longDescription || [];

    return {
      ...state,
      subscriberGroupItem: {
        ...subscriberGroupItem,
        longDescription: longDescription.some(longDescriptionItem => longDescriptionItem.value === action.languageId)
          ? longDescription.map(
            longDescriptionItem => longDescriptionItem.value === action.languageId
              ? {
                ...longDescriptionItem,
                description: action.description
              }
              : longDescriptionItem
          )
          : [
            ...longDescription,
            {
              value: action.languageId,
              description: action.description
            }
          ]
      },
      validation: clearValidationForSingleParameter(
        state.validation,
        'longDescription',
        longDescriptionValidationParameter,
        action.languageId
      )
    };
  },

  [ACTION.COPY_LONG_DESCRIPTION_TO_ALL]: (state: SubscriberGroupsFormState,
    action: CopyLongDescriptionToAll): SubscriberGroupsFormState => {

    const subscriberGroupItem = state.subscriberGroupItem || {} as SubscriberGroupItem;
    const longDescription = subscriberGroupItem.longDescription || [];
    const targetLongDescription = longDescription.find(longDescriptionItem => longDescriptionItem.value === action.languageId);

    if (targetLongDescription === undefined) {
      return state;
    }

    const updatedlongDescription = longDescription.map(
      languageItem => ({
        ...languageItem,
        description: targetLongDescription.description
      })
    );
    const newlongDescription = (state.languageOptions || [])
      .map(option => option.value)
      .filter(languageId => !longDescription.some(languageItem => languageItem.value === languageId))
      .map(languageId => ({
        value: languageId,
        description: targetLongDescription.description
      }));

    return {
      ...state,
      subscriberGroupItem: {
        ...subscriberGroupItem,
        longDescription: [
          ...updatedlongDescription,
          ...newlongDescription
        ]
      },
      validation: clearValidationForAllParameters(state.validation, 'longDescription', longDescriptionValidationParameter)
    };
  },

  [ACTION.UPDATE_ITEM]: (state: SubscriberGroupsFormState,
    action: UpdateItem): SubscriberGroupsFormState => {

    const subscriberGroupItem = {
      ...(state.subscriberGroupItem || {}),
      ...action.item
    } as SubscriberGroupItem;

    const validation = clearValidationForKeys(state.validation, Object.keys(action.item) as any[]);

    return {
      ...state,
      subscriberGroupItem,
      validation
    };
  },

  [ACTION.CLEAR_FORM]: (state: SubscriberGroupsFormState,
    action: ClearForm): SubscriberGroupsFormState => {

    return {
      ...state,
      ...INITIAL_STATE,
      languageOptions: state.languageOptions
    };
  },

  [ACTION.UPDATE_VALIDATION]: (state: SubscriberGroupsFormState,
    action: UpdateValidation): SubscriberGroupsFormState => {
    return {
      ...state,
      validation: { ...state.validation, ...action.validation }
    };
  },

  [ACTION.UPDATE_EXTERNAL_MEMBER_ITEM]: (state: SubscriberGroupsFormState,
    action: UpdateExternalMemberItem): SubscriberGroupsFormState => {

    const subscriberGroupItem = {
      ...(state.subscriberGroupItem || {}),
      externalMembers: state.subscriberGroupItem.externalMembers.map(item => {

        if (item.memberNo === action.externalMemberValue.memberNo) {
          return action.externalMemberValue;
        }

        return item;
      })
    } as SubscriberGroupItem;

    return {
      ...state,
      subscriberGroupItem
    };
  },

  [ACTION.REMOVE_EXTERNAL_MEMBER]: (state: SubscriberGroupsFormState,
    action: RemoveExternalMember): SubscriberGroupsFormState => {

    const subscriberGroupItem = {
      ...(state.subscriberGroupItem || {}),
      externalMembers: state.subscriberGroupItem.externalMembers.filter(text => text.memberNo !== action.id) ?? []
    } as SubscriberGroupItem;

    return {
      ...state,
      subscriberGroupItem: subscriberGroupItem
    };
  },

  [ACTION.REMOVE_ALL_EXTERNAL_MEMBERS]: (state: SubscriberGroupsFormState,
    action: RemoveAllExternalMembers): SubscriberGroupsFormState => {

    const subscriberGroupItem = {
      ...(state.subscriberGroupItem || {}),
      externalMembers: []
    } as SubscriberGroupItem;

    return {
      ...state,
      subscriberGroupItem: subscriberGroupItem
    };
  },

  [ACTION.ADD_EXTERNAL_MEMBER]: (state: SubscriberGroupsFormState, action: AddExternalMember): SubscriberGroupsFormState => {

    const externalMember = state.subscriberGroupItem.externalMembers ?? [];
    const id = createId(externalMember);

    return {
      ...state,
      subscriberGroupItem: {
        ...state.subscriberGroupItem,
        externalMembers: externalMember.concat(createInitialExternalMember(id))
      }
    };
  },

  [ACTION.UPDATE_MERGE_SUBSCRIBER_GROUP]: (state: SubscriberGroupsFormState,
    action: UpdateMergeSubscriberGroup): SubscriberGroupsFormState => {

    return {
      ...state,
      mergeSubscriberGroup: action.mergeSubscriberGroup
    };
  },

  [ACTION.REQUEST_SUBSCRIBER_GROUP_OPTIONS]: (state: SubscriberGroupsFormState,
    action: RequestSubscriberGroupOptions): SubscriberGroupsFormState => {
    return {
      ...state,
      subscriberGroupOptions: {
        fetching: true,
        values: []
      }
    };
  },

  [ACTION.RECEIVE_SUBSCRIBER_GROUP_OPTIONS]: (state: SubscriberGroupsFormState,
    action: ReceiveSubscriberGroupOptions): SubscriberGroupsFormState => {
    return {
      ...state,
      subscriberGroupOptions: {
        fetching: false,
        values: action.values
      }
    };
  },

  [ACTION.FAIL_SUBSCRIBER_GROUP_OPTIONS]: (state: SubscriberGroupsFormState,
    action: FailSubscriberGroupOptions): SubscriberGroupsFormState => {
    return {
      ...state,
      subscriberGroupOptions: {
        fetching: false,
        values: []
      }
    };
  },

  [ACTION.REQUEST_SUBSCRIBER_GROUP_ITEM_DELETE]: (state: SubscriberGroupsFormState,
    action: RequestSubscriberGroupItemDelete): SubscriberGroupsFormState => {
    return {
      ...state,
      mergeSubscriberGroup: {
        ...state.mergeSubscriberGroup,
        fetching: true
      }
    };
  },

  [ACTION.RECEIVE_SUBSCRIBER_GROUP_ITEM_DELETE]: (state: SubscriberGroupsFormState,
    action: ReceiveSubscriberGroupItemDelete): SubscriberGroupsFormState => {
    return {
      ...state,
      mergeSubscriberGroup: {
        ...state.mergeSubscriberGroup,
        fetching: false
      }
    };
  },

  [ACTION.FAIL_SUBSCRIBER_GROUP_ITEM_DELETE]: (state: SubscriberGroupsFormState,
    action: FailSubscriberGroupItemDelete): SubscriberGroupsFormState => {
    return {
      ...state,
      mergeSubscriberGroup: {
        ...state.mergeSubscriberGroup,
        fetching: false
      }
    };
  },

  [ACTION.RESET_VALIDATION]: (state: SubscriberGroupsFormState,
    action: ResetValidation): SubscriberGroupsFormState => {

    const validation = { ...state.validation, [action.key]: undefined };

    return {
      ...state,
      validation
    };
  },

  [ACTION.RESET_ALL_EXTERNAL_MEMBER_VALIDATION]: (state: SubscriberGroupsFormState,
    action: ResetAllExternalMemberValidation): SubscriberGroupsFormState => {
    return {
      ...state,
      validation: {
        ...state.validation,
        emailAddress: undefined,
        name: undefined
      }
    };
  },

  [ACTION.UPDATE_VALIDATION]: (
    state: SubscriberGroupsFormState,
    action: UpdateValidation
  ): SubscriberGroupsFormState => {

    return {
      ...state,
      validation: { ...state.validation, ...action.validation }
    };
  }
};

const stateReducer: Reducer<SubscriberGroupsFormState> = (
  state: SubscriberGroupsFormState = INITIAL_STATE, action: Action) => {
  const r = reducers[action.type];
  return (r && r(state, action)) || state;
};

export const reducer = withOptionsReducer(STATE.subscriberGroups, stateReducer);
